import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Checkbox, TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import SpecialistApi from "../../../api/specialistsApi";
import { ReactComponent as MergeIcon } from "../../../assets/Certificates/merge.svg";
import { Federation } from "../../../types/TypeSpecialist";
import { getTranslation } from "../../../utils/getTranslation";
import Notify from "../../../utils/toaster";
import MergeModal from "./MergeModal";
import s from "./Tables.module.css";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon color="success" />
        ) : (
          <FirstPageIcon color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" />
        ) : (
          <KeyboardArrowLeft color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" />
        ) : (
          <KeyboardArrowRight color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" />
        ) : (
          <LastPageIcon color="success" />
        )}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",
    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#2E2E2E",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#242424",
    border: 0,
  },
  // hide last border
  "td, th": {
    border: 0,
  },
}));

type SpecialistTableProps = {
  rows: Federation[];
  page: number;
  rowsPerPage: number;
  setPage: (value: number) => void;
  setRowsPerPage: (value: number) => void;
  selectHandler: (id: string) => void;
  count: number;
  setUpdateDataTriger: React.Dispatch<React.SetStateAction<number>>;
};
export default function FederationTable({
  rows,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  selectHandler,
  count,
  setUpdateDataTriger,
}: SpecialistTableProps) {
  const token = localStorage.getItem("token");
  const [idsToMerge, setIdsToMerge] = React.useState<string[]>([]);
  const [mergeModalIsOpen, setMergeModalIsOpen] = React.useState(false);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const mergeCertificates = async (
    title: { text: string; language: string }[]
  ) => {
    if (!token) return;
    const payload = {
      federationIds: idsToMerge,
      updatedText: title,
    };
    setMergeModalIsOpen(false);
    const response = await SpecialistApi.mergeFederations(token, payload);
    if (!response.status && response.message) {
      Notify(response.message);
    }
    if (response.status) {
      setUpdateDataTriger((prev) => prev + 1);
      setIdsToMerge([]);
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        minWidth: 700,
        width: "100%",
        borderRadius: "20px",
        backgroundColor: "transparent",
      }}
    >
      <Table
        sx={{ minWidth: 700, width: "100%" }}
        aria-label="customized table"
      >
        <TableHead className={s.tableHead}>
          <TableRow>
            <StyledTableCell align="left">Title</StyledTableCell>
            <StyledTableCell align="left">Specialization</StyledTableCell>
            <StyledTableCell align="left">Status</StyledTableCell>
            <StyledTableCell align="left">
              <div className={s.mergeblock}>
                <span>Merge</span>

                <div
                  className={s.mergeBtn}
                  role="button"
                  style={{ opacity: idsToMerge.length > 1 ? 1 : 0.3 }}
                  onClick={() =>
                    idsToMerge.length > 1 ? setMergeModalIsOpen(true) : {}
                  }
                >
                  <MergeIcon />
                </div>
              </div>
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <StyledTableRow
              key={row._id}
              sx={{
                cursor: "pointer",
              }}
            >
              <StyledTableCell
                align="left"
                className={s.nameColumn}
                onClick={() => selectHandler(row._id)}
              >
                {getTranslation(row.text, "en")}
              </StyledTableCell>
              <StyledTableCell
                align="left"
                className={s.emailColumn}
                onClick={() => selectHandler(row._id)}
              >
                {row.specialization.specialization}
              </StyledTableCell>
              <StyledTableCell
                align="left"
                onClick={() => selectHandler(row._id)}
              >
                <span
                  className={
                    row.isShown ? s.verifiedColumn : s.unverifiedColumn
                  }
                >
                  {row.isShown ? "Verified" : "Not verified"}
                </span>
              </StyledTableCell>
              <StyledTableCell align="left">
                <Checkbox
                  checked={idsToMerge.includes(row._id)}
                  onChange={() => {
                    setIdsToMerge((prev) =>
                      prev.includes(row._id)
                        ? prev.filter((el) => el !== row._id)
                        : [...prev, row._id]
                    );
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                  sx={{
                    color: "#fff",
                    "&.Mui-checked": {
                      color: "#3fb082",
                    },
                  }}
                />
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
        <TableFooter className={s.tableFooter}>
          <TableRow
            sx={{
              minWidth: 700,
              width: "100%",
              border: "none",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 10, 15]}
              colSpan={4}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <MergeModal
        setModalIsOpen={setMergeModalIsOpen}
        mergeFunction={mergeCertificates}
        modalIsOpen={mergeModalIsOpen}
      />
    </TableContainer>
  );
}
